// SpeciesTraitsUpload.jsx
// Component for adding base personality traits to species documents

import React, { useState } from "react";
import { updateDB } from "../../DatabaseOperations";

// React Components
import ConsoleHomePageHeader from "../Home/ConsoleHomePageHeader";

// CSS
import "../SpeciesInfoUpload/PlantEditor.css";

// Common species options (you can expand this list)
const commonSpecies = [
  { value: "Ficus lyrata", label: "Ficus Lyrata (Fiddle Leaf Fig)" },
  {
    value: "Dracaena trifasciata",
    label: "Dracaena Trifasciata (Snake Plant)",
  },
  {
    value: "Pilea peperomioides",
    label: "Pilea Peperomioides (Chinese Money Plant)",
  },
  { value: "Monstera deliciosa", label: "Monstera Deliciosa" },
  { value: "Epipremnum aureum", label: "Pothos (Epipremnum aureum)" },
  { value: "Philodendron hederaceum", label: "Philodendron Hederaceum" },
  {
    value: "Zamioculcas zamiifolia",
    label: "ZZ Plant (Zamioculcas zamiifolia)",
  },
];

const SpeciesTraitsUpload = () => {
  // Hooks for form data
  const [speciesId, setSpeciesId] = useState("");
  const [humor, setHumor] = useState(2);
  const [chaos, setChaos] = useState(2);
  const [evil, setEvil] = useState(2);
  const [sass, setSass] = useState(2);
  const [charm, setCharm] = useState(2);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const uploadSpeciesTraits = async () => {
    if (!speciesId.trim()) {
      setError("Please select or enter a species ID");
      return;
    }

    setLoading(true);
    setError("");
    setResult(null);

    try {
      // Create the basePlantsonality object
      const basePlantsonality = {
        traits: {
          humor: Number(humor),
          chaos: Number(chaos),
          evil: Number(evil),
          sass: Number(sass),
          charm: Number(charm),
        },
      };

      // Update the species document
      await updateDB(speciesId, "Species", {
        basePlantsonality: basePlantsonality,
      });

      setResult({
        speciesId,
        traits: basePlantsonality.traits,
      });
    } catch (err) {
      setError(`Failed to update species: ${err.message}`);
      console.error("Error updating species:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSpeciesId("");
    setHumor(2);
    setChaos(2);
    setEvil(2);
    setSass(2);
    setCharm(2);
    setResult(null);
    setError("");
  };

  return (
    <div>
      <ConsoleHomePageHeader />
      <div className="title">Species Base Traits Upload</div>

      <div style={{ maxWidth: "600px", margin: "0 auto", padding: "20px" }}>
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
            }}
          >
            Species ID:
          </label>
          <select
            value={speciesId}
            onChange={(e) => setSpeciesId(e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              marginBottom: "10px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          >
            <option value="">Select a species...</option>
            {commonSpecies.map((species) => (
              <option key={species.id} value={species.id}>
                {species.name}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Or enter custom species ID"
            value={speciesId}
            onChange={(e) => setSpeciesId(e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              borderRadius: "4px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h3>Personality Traits (1-5 scale)</h3>
          <p style={{ color: "#666", fontSize: "14px" }}>
            Set the base personality traits for this species. These will be used
            as defaults when creating new plants.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
            marginBottom: "20px",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
              }}
            >
              Humor: {humor}
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={humor}
              onChange={(e) => setHumor(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
              }}
            >
              Chaos: {chaos}
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={chaos}
              onChange={(e) => setChaos(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
              }}
            >
              Evil: {evil}
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={evil}
              onChange={(e) => setEvil(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
              }}
            >
              Sass: {sass}
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={sass}
              onChange={(e) => setSass(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>

          <div style={{ gridColumn: "span 2" }}>
            <label
              style={{
                display: "block",
                marginBottom: "5px",
                fontWeight: "bold",
              }}
            >
              Charm: {charm}
            </label>
            <input
              type="range"
              min="1"
              max="5"
              value={charm}
              onChange={(e) => setCharm(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
          <button
            onClick={uploadSpeciesTraits}
            disabled={loading}
            style={{
              padding: "10px 20px",
              backgroundColor: loading ? "#ccc" : "#4CAF50",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: loading ? "not-allowed" : "pointer",
              flex: 1,
            }}
          >
            {loading ? "Uploading..." : "Upload Base Traits"}
          </button>

          <button
            onClick={resetForm}
            style={{
              padding: "10px 20px",
              backgroundColor: "#f44336",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Reset
          </button>
        </div>

        {error && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#ffebee",
              color: "#c62828",
              borderRadius: "4px",
              marginBottom: "20px",
              border: "1px solid #ffcdd2",
            }}
          >
            {error}
          </div>
        )}

        {result && (
          <div
            style={{
              padding: "20px",
              backgroundColor: "#e8f5e8",
              borderRadius: "10px",
              border: "2px solid #4caf50",
            }}
          >
            <h3 style={{ color: "#2e7d32", marginTop: 0 }}>
              Base Traits Updated Successfully! 🌱
            </h3>

            <div style={{ marginBottom: "15px" }}>
              <strong>Species ID:</strong>
              <code
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "2px 6px",
                  borderRadius: "3px",
                  marginLeft: "8px",
                }}
              >
                {result.speciesId}
              </code>
            </div>

            <div>
              <strong>Base Personality Traits:</strong>
              <ul style={{ marginTop: "5px" }}>
                {Object.entries(result.traits).map(([trait, value]) => (
                  <li key={trait}>
                    {trait.charAt(0).toUpperCase() + trait.slice(1)}: {value}
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ marginTop: "15px", fontSize: "14px", color: "#555" }}>
              <strong>Note:</strong> These base traits will now be used as
              defaults when creating new plants of this species.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeciesTraitsUpload;
